/**
 * Cloze production — the tier-2 workhorse (TASKS T49, SPEC §5, §2 `modes/cloze.ts`).
 *
 * A word is cut out of a real sentence and the learner types it back. This is retrieval, not
 * recognition: nothing on the card contains the answer, there is no set of options, and an empty box
 * is a real graded failure rather than a skip (RESEARCH §1, invariant 3).
 *
 * Tier 2 is "the top ~300 words" but a bare word cannot be presented — `el` on its own asks nothing.
 * So the item carries the word in `es`, which is what grading compares against, and the corpus
 * carries the sentence it lives in (`Example`, DECISIONS D-072). This module's real work is finding
 * the word *inside* that sentence so the hole lands in the right place, which is fiddlier than it
 * looks: the sentence capitalises it at the start (`El niño ya duerme.`), the target may be two words
 * (`hay que`), and `él` and `el` are different items whose sentences sit next to each other in the
 * corpus.
 *
 * Pure. No DOM, no clock, no store — the cut is a string operation and is unit-tested as one.
 */

import { stripAccents } from '../lang/accents';
import type { ModeInput, Presentation, PromptSegment } from './types';

/** A sentence with one word taken out of it. `answer` is the surface form that was removed. */
export interface ClozeCut {
  before: string;
  /** Exactly as it appeared in the sentence, capitalisation and accents included. */
  answer: string;
  after: string;
}

interface Word {
  text: string;
  start: number;
  end: number;
}

/** Letters and combining marks. Punctuation, spaces, and the inverted marks are not part of a word. */
const WORD_RE = /[\p{L}\p{M}]+/gu;

function words(text: string): Word[] {
  const out: Word[] = [];
  for (const match of text.matchAll(WORD_RE)) {
    if (match.index === undefined) continue;
    out.push({ text: match[0], start: match.index, end: match.index + match[0].length });
  }
  return out;
}

/**
 * Find `target` in `sentence` and cut it out.
 *
 * Two passes, and the order is the whole point. The first compares case-insensitively but
 * **accent-sensitively**, so the pronoun `él` is not matched by the article `el`; only if that finds
 * nothing does the second pass fold accents too, which rescues a carrier sentence whose accent was
 * typed differently from the target's. Getting this backwards would put the hole in the wrong word in
 * a sentence containing both — and the learner would be marked wrong for answering the question the
 * card appeared to ask.
 *
 * Returns null when the target is not in the sentence at all. The build-time shape check
 * (`content/schema.ts`) already refuses such an item, so this is the belt to that braces: a null here
 * costs the learner a carrier sentence, never the item.
 */
export function cutCloze(sentence: string, target: string): ClozeCut | null {
  const haystack = words(sentence);
  const needle = words(target);
  if (needle.length === 0 || haystack.length < needle.length) return null;

  const folds: ((value: string) => string)[] = [
    (value) => value.toLowerCase(),
    (value) => stripAccents(value).toLowerCase(),
  ];

  for (const fold of folds) {
    const wanted = needle.map((word) => fold(word.text));
    for (let i = 0; i + wanted.length <= haystack.length; i += 1) {
      const matches = wanted.every((word, j) => fold(haystack[i + j]!.text) === word);
      if (!matches) continue;
      const from = haystack[i]!.start;
      const to = haystack[i + wanted.length - 1]!.end;
      return {
        before: sentence.slice(0, from),
        answer: sentence.slice(from, to),
        after: sentence.slice(to),
      };
    }
  }

  return null;
}

/**
 * The help this card offers if asked for it (invariant 2 — asking marks the attempt hinted, T54).
 *
 * The first letter and the shape, never a letter count for a multi-word target: "6 letters" for
 * `hay que` is a puzzle rather than a hint. It stops short of the second letter deliberately — a
 * scaffold that reconstructs the word turns a failed retrieval into a copying exercise, and the
 * evidence that retrieval is what works is the one thing this whole game is built on (RESEARCH §1).
 */
export function clozeScaffold(target: string): string | null {
  const parts = words(target);
  const first = parts[0];
  if (!first) return null;
  const letter = [...first.text][0]!;
  if (parts.length > 1) return `Starts with "${letter}" — ${parts.length} words.`;
  const count = [...first.text].length;
  return `Starts with "${letter}" — ${count === 1 ? '1 letter' : `${count} letters`}.`;
}

/** Letters in the answer, for the blank's `length`. Never rendered — see `PromptSegment`. */
function letterCount(target: string): number {
  return words(target).reduce((total, word) => total + [...word.text].length, 0);
}

/**
 * Build the cloze card.
 *
 * The English is always shown, and has to be: `___ niño ya duerme.` is answerable by `el`, `un`,
 * `ese`, or `mi`, and a card with several right answers that accepts one of them is not a test of
 * Spanish. With the gloss it is a single-answer production task.
 *
 * With no carrier sentence — an item minted from a conversation log, or a corpus entry whose example
 * was lost — the card degrades to a plain translation prompt rather than refusing to render. The
 * item is due either way, and invariant 7 says a review is never withheld.
 */
export function clozePresentation(input: ModeInput): Presentation {
  const { item, extras } = input;
  const sentence = extras.example?.es;
  const cut = sentence ? cutCloze(sentence, item.es) : null;
  const scaffold = clozeScaffold(item.es);

  if (!cut) {
    return {
      mode: 'cloze',
      instruction: 'Say it in Spanish',
      segments: [{ kind: 'text', text: item.en }],
      gloss: null,
      expected: item.es,
      scaffold,
      input: 'text',
      fieldLabel: 'Your answer',
      announce: `${item.en}. Type it in Spanish.`,
    };
  }

  const gloss = extras.example?.en ?? item.en;
  const segments: PromptSegment[] = [];
  if (cut.before) segments.push({ kind: 'text', text: cut.before });
  segments.push({ kind: 'blank', length: letterCount(item.es) });
  if (cut.after) segments.push({ kind: 'text', text: cut.after });

  return {
    mode: 'cloze',
    instruction: 'Type the missing word',
    segments,
    gloss,
    expected: item.es,
    scaffold,
    input: 'text',
    fieldLabel: 'The missing word',
    // Spoken, not shown: a screen reader gets the sentence with the gap named, then the English.
    // Built here rather than from the segments so the gap is the word "blank" and not six
    // underscores read out one at a time.
    announce: `${cut.before}blank${cut.after} ${gloss}`.replace(/\s+/g, ' ').trim(),
  };
}
